import { trigger, transition, style, animate } from "@angular/animations";
import { Component, OnInit, OnDestroy, ViewChildren, QueryList, ViewChild, Input, inject } from "@angular/core";
import { FormGroup, FormControl, Validators } from "@angular/forms";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { SharedService } from "@shared/services/shared.service";
import { AddressComponent } from "app/components/reusable/address/address.component";
import { DocumentComponent } from "app/components/reusable/document/document.component";
import { OfficeHoursComponent } from "app/components/reusable/office-hours/office-hours.component";
import { ISatelliteLocation } from "app/models/licenses/hospital/satellite-location";
import { AddressService } from "app/services/address/address.service";
import { DocumentService } from "app/services/document/document.service";
import { SatelliteLocationService } from "app/services/license/hospital/satellite-location.service";
import { OfficeHoursService } from "app/services/license/office-hours.service";
import { firstValueFrom } from "rxjs";

@Component({
  selector: 'satellite-location',
  templateUrl: './satellite-location.component.html',
  styleUrls: ['./satellite-location.component.scss'],
    animations: [
      trigger('slideRight', [
        transition(':enter', [
          style({ transform: 'translateX(100%)' }),
          animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
        ]),
        transition(':leave', [
          animate('200ms ease-in', style({ transform: 'translateX(100%)' }))
        ])
      ])
    ]
})

export class SatelliteLocationComponent implements OnInit, OnDestroy {
  @ViewChildren(DocumentComponent) documents!: QueryList<DocumentComponent>;
  @ViewChildren(AddressComponent) addresses!: QueryList<AddressComponent>;
  @ViewChild(OfficeHoursComponent) officeHours!: OfficeHoursComponent;
  @Input() canEdit: boolean = false
  @Input() entityId: number = 0;
  @Input() entityType: string = '';
  @Input() isChange: boolean = false;
  @Input() HospitalLicenseId: string = "";

  public entity: string = 'SatelliteLocation';
  public deletingSatelliteLocation: boolean = false;
  public managingSatelliteLocation: boolean = false;
  public isOutOfState: boolean = false;
  public newSatelliteCreated: boolean = false;
  public includeArchived: boolean = false;
  public satelliteLocationTypes: string[] = ['Map of Location', 'Other'];

  public satelliteLocation: ISatelliteLocation = {
    id: 0,
    satelliteId: "",
    buildingName: "",
    phone: "",
    parentId: 0,
    archived: false,
    addresses: [],
    documents: []
  }

  public satellites: ISatelliteLocation[] = [];
  public satelliteForm = new FormGroup({
    satelliteId: new FormControl('', Validators.required),
    buildingName: new FormControl('', Validators.required),
    phone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)])
  });

  public satelliteLocationColumns: string [] = ['satelliteId', 'buildingName', 'archived'];
  public satelliteLocationSource = new MatTableDataSource<ISatelliteLocation>(this.satellites);
  @ViewChild('satelliteLocationPaginator', {static: false}) satelliteLocationPaginator!: MatPaginator;
  @ViewChild('satelliteLocationSort', {static: false}) satelliteLocationSort!: MatSort;

  public sharedService = inject(SharedService);
  public satelliteLocationService = inject(SatelliteLocationService);
  public addressService = inject(AddressService);
  public officeHoursService = inject(OfficeHoursService);
  public documentService = inject(DocumentService);

  ngOnInit(): void {
    this.getSatelliteLocations(this.includeArchived);
  }

  ngOnDestroy(): void {
    if(this.managingSatelliteLocation){
      this.updateSatelliteLocation();
    }
  }

  getSatelliteLocations(includeArchived: boolean): void {
    this.includeArchived = includeArchived;

    this.satelliteLocationService.GetSatelliteLocations(this.entityId, this.entityType, this.includeArchived).subscribe({
      next: response => this.satellites = response,
      complete: () => this.updateSatelliteTable()
    });
  }

  updateSatelliteTable(): void {
    setTimeout(() => {
      this.satelliteLocationSource.data = this.satellites;
      this.satelliteLocationSource.paginator = this.satelliteLocationPaginator;
      this.satelliteLocationSource.sort = this.satelliteLocationSort;
    }, 100);
  }

  selectSatelliteLocation(satelliteLocation: ISatelliteLocation): void {
    this.deletingSatelliteLocation = false;
    this.satelliteLocation = satelliteLocation;
    this.managingSatelliteLocation = true;
    this.toggleSatelliteForm(true);
    this.updateSatelliteForm(satelliteLocation);
  }

  toggleSatelliteForm(adding: boolean): void{
    this.managingSatelliteLocation = adding;
    this.updateSatelliteForm(this.getEmptySatelliteLocation());
    if(!adding){
      this.updateSatelliteTable();
    }
  }

  getEmptySatelliteLocation(): ISatelliteLocation {
    return {
      id: 0,
      satelliteId: "",
      buildingName: "",
      phone: "",
      parentId: this.entityId,
      archived: false,
      addresses: [],
      documents: []
    }
  }

  updateSatelliteForm(satelliteLocation: ISatelliteLocation): void {
    this.satelliteForm.reset({
      satelliteId: satelliteLocation.satelliteId,
      buildingName: satelliteLocation.buildingName,
      phone: satelliteLocation.phone
    });
  }

  updateSatelliteLocation(): void {
    let form = this.satelliteForm.getRawValue();
    this.satelliteLocation.satelliteId = form.satelliteId ?? '';
    this.satelliteLocation.buildingName = form.buildingName ?? '';
    this.satelliteLocation.phone = form.phone ?? '';
  }

   createSatelliteLocation(): void {
    this.satelliteLocation = this.getEmptySatelliteLocation();
    this.satelliteLocation.parentId = this.entityId;
    this.managingSatelliteLocation = true;
     this.satelliteLocationService.createSatelliteLocation(this.satelliteLocation, this.entityId, this.entityType).subscribe({
       next: response => {
         this.satellites.push(response);
         this.satelliteLocation = response;
       },
       error: e => {
         this.sharedService.toastr.error("Error Creating Satellite Location");
         console.log('error creating Satellite Location', e);
       },
       complete: () => {
        this.toggleSatelliteForm(true);
        this.newSatelliteCreated = true;
        this.deletingSatelliteLocation = false;
       }
     });
   }

   saveSatelliteLocation(): void {
     this.updateSatelliteLocation();
     this.satelliteLocationService.updateSatelliteLocation(this.satelliteLocation).subscribe({
       complete: () => {
         this.newSatelliteCreated = false;
         this.toggleSatelliteForm(false);
       },
       error: e => {
         this.sharedService.toastr.error("Could Not Save Satellite Location");
         console.log('error updating Satellite Location', e);
       }
     });
   }

   archiveSatelliteLocation(satelliteLocation: ISatelliteLocation, archive: boolean): void {
     let archiving: string = archive? 'Archive ' : 'Un-Archive ';
     this.sharedService.openConfirm(archiving + 'this Satellite Location?');
     this.sharedService.confirmed().subscribe(
       confirmed => {
         if(confirmed){
           this.satelliteLocationService.archiveSatelliteLocation(satelliteLocation.id, archive).subscribe({
             complete: () => {
              satelliteLocation.archived = archive;
               if(archive){
                 this.satellites = this.satellites.filter(o => o.id !== satelliteLocation.id);
                 this.satelliteLocationSource.data = this.satellites;
                 this.satelliteLocationSource.paginator = this.satelliteLocationPaginator;
                 this.satelliteLocationSource.sort = this.satelliteLocationSort;
               }
               this.toggleSatelliteForm(false);
             },
             error: e => {
               this.sharedService.toastr.error("Could Not Archive Satellite");
               console.log('error archiving satellite', e);
             }
           });
         }
       }
     );
   }

   confirmDelete(satelliteLocationId: number): void {
     this.sharedService.openConfirm('Delete Satellite Location ' + this.satelliteLocation.buildingName + ' ?');
     this.sharedService.confirmed().subscribe(
       confirmed => {
         if(confirmed){
           this.deletingSatelliteLocation = true;
           this.deleteSatelliteLocation(satelliteLocationId);
         }
       }
     );
   }

   deleteSatelliteLocation(satelliteLocationId: number): void {
     this.satelliteLocationService.deleteSatelliteLocation(satelliteLocationId).subscribe({
       complete: () => {
         this.newSatelliteCreated = false;
         this.satellites = this.satellites.filter(a => a.id !== satelliteLocationId);
         this.satelliteLocationSource.data = this.satellites;
         this.satelliteLocationSource.paginator = this.satelliteLocationPaginator;
         this.satelliteLocationSource.sort = this.satelliteLocationSort;
         this.toggleSatelliteForm(false);
       },
       error: e => console.log('error deleting satellite location', e)
     });
   }

  async validateDocuments(): Promise<string[]> {
     let errors: string[] = [];
     for (const satellite of this.satellites) {
      try {
        const response = await firstValueFrom(
          this.documentService.getDocuments(satellite.id, 'SatelliteLocation', false)
        );
        satellite.documents = response;
        if (satellite.documents.length === 0) {
          errors.push(`Satellite Location ${satellite.buildingName} must upload a map of the location`);
        }
      } catch (e) {
        console.log('Get document error', e);
      }
    }
     return errors;
   }

  updateOutOfState(isOutOfState: boolean): void {
    this.isOutOfState = isOutOfState;
  }

  async validateSatelliteAddresses(): Promise<string[]> {
    let errors: string[] = [];
    for (const satellite of this.satellites) {
      try {
        const response = await firstValueFrom(
          this.addressService.getAddresses(satellite.id, 'SatelliteLocation', false)
        );
        satellite.addresses = response;
        if (!satellite.addresses.find(a =>
          !a.archived && a.addressTypes.find(t => t.type === 'Physical')
        )) {
          errors.push(`Satellite Location ${satellite.buildingName} must have a physical address`);
        }
      } catch (e) {
        console.log('Get address error', e);
      }
    }

    return errors;
  }
}
